import { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { LoadFailure } from '../../components/LoadFailure';
import { PAGE_MAX_WIDTH } from '../../theme';
import { logEvent } from '../../utils/eventLog';
import { PageHeader } from '../Benchmarks/components/PageHeader';
import { downloadCsv } from '../Dashboard/assignmentMonitoring';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { TopBar } from '../IndividualPlan/TopBar';
import { BandGrid } from './components/BandGrid';
import { BandHistogram } from './components/BandHistogram';
import { ActionButton, FilterChip, LabelledSelect, SkillTabs, yearLabel } from './components/Controls';
import { GROWTH_CARD_SX, TEXT_SX } from './components/cardParts';
import { GroupTable } from './components/GroupTable';
import { ReturnTestingCard } from './components/ReturnTestingCard';
import { BenchmarkHistory, RubricIndicators, Responses } from './components/StudentCards';
import { DEFAULT_FILTERS, StudentsTable, visibleStudents, type StudentFilters } from './components/StudentsTable';
import { breadcrumbsFor, buildGroupCsv, buildTeacherCsv, formatDataAsOf, gradeRoute, laterLabel, schoolRoute, sortLeastGrowthFirst, studentRoute, teacherRoute, type Trail } from './growth';
import { GrowthNotFound, fetchGrowthPage, fetchGrowthStudent, type GrowthQuery } from './growthApi';
import { GROWTH } from './tokens';
import { SKILL_LABELS, type GroupBy, type GrowthLevel, type GrowthPage as Page, type GrowthPeriod, type GrowthRow, type GrowthStudent, type Skill } from './types';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export type PageSource = (query: GrowthQuery) => Promise<Page>;
export type StudentSource = (studentId: string, year?: number) => Promise<GrowthStudent>;

type TrailState = { viaGrade?: string; school?: string; teacher?: string; year?: number; group?: GroupBy };

type Loaded<T> = { kind: 'ready'; data: T } | { kind: 'notFound' } | { kind: 'failed' };

type Settled<T> = { key: string; loaded: Loaded<T> };

type YearChoice = 'this' | 'last';

const GROUP_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'grade', label: 'Grade' },
  { value: 'band', label: 'Grade band' },
  { value: 'school', label: 'School' },
];

const FIRST_COLUMN: Record<GroupBy, string> = { grade: 'Grade', band: 'Grade band', school: 'School' };

function groupOf(param: string | null): GroupBy {
  return param === 'school' || param === 'band' ? param : 'grade';
}

function shownSchools(page: Page, skill: Skill): GrowthRow[] {
  return (page.bySchool ?? []).filter((row) => row.skills[skill].matched > 0);
}

const YEAR_CHOICES: { value: YearChoice; label: string }[] = [
  { value: 'this', label: 'This year' },
  { value: 'last', label: 'Last year' },
];

const NOT_FOUND: Record<GrowthLevel | 'student', string> = {
  district: 'Flashlight360 results are not available for this district.',
  grade: 'Grade not found.',
  school: 'School not found.',
  teacher: 'Teacher not found.',
  student: 'Student not found.',
};

export function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function trailState(state: unknown): TrailState {
  return typeof state === 'object' && state !== null ? (state as TrailState) : {};
}

function useLoaded<T>(key: string, load: (key: string) => Promise<T>): Loaded<T> | { kind: 'loading' } {
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  useEffect(() => {
    let active = true;
    load(key)
      .then((data) => {
        if (active) setSettled({ key, loaded: { kind: 'ready', data } });
      })
      .catch((err: unknown) => {
        if (!active) return;
        setSettled({ key, loaded: { kind: err instanceof GrowthNotFound ? 'notFound' : 'failed' } });
      });
    return () => {
      active = false;
    };
  }, [key, load]);
  return settled?.key === key ? settled.loaded : { kind: 'loading' };
}

function Shell({ crumbs, children }: { crumbs?: Trail; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: GROWTH.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="dashboard" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {crumbs && <TopBar crumbs={breadcrumbsFor(crumbs)} />}
        <Box
          data-testid="growth-content"
          sx={{ p: '24px 24px 72px', maxWidth: PAGE_MAX_WIDTH + 48, width: '100%', mx: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '24px' }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: '64px' }}>
      <CircularProgress />
    </Box>
  );
}

function NotFound({ text }: { text: string }) {
  return (
    <Typography data-testid="growth-not-found" sx={TEXT_SX}>
      {text}
    </Typography>
  );
}

function NoResults() {
  return (
    <Box component="section" data-testid="growth-no-results" sx={GROWTH_CARD_SX}>
      <Typography component="p" sx={{ fontFamily: GROWTH.font, fontSize: '24px', lineHeight: '32px', fontWeight: 700, color: GROWTH.muted, m: 0 }}>
        No results yet
      </Typography>
      <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, m: '4px 0 0' }}>
        Growth appears once students have an eligible BOY score and a later benchmark score.
      </Typography>
    </Box>
  );
}

function subtitleOf(subtitle: string, period: GrowthPeriod | null, dataAsOf: string): string {
  if (period?.lastYear) return `${subtitle} · ${period.label} final`;
  const stamp = formatDataAsOf(dataAsOf);
  return stamp ? `${subtitle} · ${stamp}` : subtitle;
}

function yearChoiceOf(page: Page): YearChoice | null {
  if (!page.period || page.currentYear === 0) return null;
  if (page.period.schoolYear === page.currentYear) return 'this';
  if (page.period.schoolYear === page.currentYear - 1) return 'last';
  return null;
}

function queryFor(level: GrowthLevel, params: { grade?: string; school?: string; teacher?: string }, gradeParam: string | undefined, schoolParam: string | undefined, year: number | undefined): GrowthQuery {
  const query: GrowthQuery = { level };
  if (level === 'grade' && params.grade) query.grade = params.grade;
  if (level === 'school') {
    if (params.school) query.school = params.school;
    if (gradeParam) query.grade = gradeParam;
  }
  if (level === 'teacher') {
    if (params.teacher) query.teacher = params.teacher;
    if (schoolParam) query.school = schoolParam;
  }
  if (year) query.year = year;
  return query;
}

export function GrowthPage({ level, source = fetchGrowthPage }: { level: GrowthLevel; source?: PageSource }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = trailState(location.state);
  const gradeParam = searchParams.get('grade') ?? undefined;
  const schoolParam = searchParams.get('school') ?? undefined;
  const [yearChoice, setYearChoice] = useState<{ at: string; year: number | undefined }>({ at: location.key, year: state.year });
  const year = yearChoice.at === location.key ? yearChoice.year : state.year;
  const setYear = (chosen: number | undefined) => setYearChoice({ at: location.key, year: chosen });
  const [skill, setSkill] = useState<Skill>('speaking');
  const query = queryFor(level, params, gradeParam, schoolParam, year);
  const key = JSON.stringify(query);
  const load = useCallback((encoded: string) => source(JSON.parse(encoded) as GrowthQuery), [source]);
  const loaded = useLoaded(key, load);
  const requested = level === 'district' ? groupOf(searchParams.get('group')) : 'grade';
  const group: GroupBy = requested === 'school' && (loaded.kind !== 'ready' || loaded.data.bySchool === null) ? 'grade' : requested;
  const setGroup = (next: GroupBy) => setSearchParams(next === 'grade' ? {} : { group: next }, { replace: true });
  const viaGrade = state.viaGrade ?? (level === 'school' ? gradeParam : undefined);
  const school = level === 'school' ? params.school : level === 'teacher' ? schoolParam : undefined;
  const crumbs: Trail | undefined =
    loaded.kind === 'ready'
      ? { level, title: loaded.data.title, grade: params.grade, school, teacher: params.teacher, viaGrade, year: year ?? loaded.data.period?.schoolYear, group: state.group }
      : undefined;

  const shownYear = year ?? (loaded.kind === 'ready' ? loaded.data.period?.schoolYear : undefined);
  const open = (row: GrowthRow) => {
    if (level === 'district' && group === 'school') {
      void logEvent('growth.school_opened.v1').catch(() => {});
      navigate(schoolRoute(row.key), { state: { year: shownYear, group } });
    } else if (level === 'district') {
      void logEvent('growth.grade_opened.v1').catch(() => {});
      navigate(gradeRoute(row.key), { state: { year: shownYear } });
    } else if (level === 'grade') {
      void logEvent('growth.school_opened.v1').catch(() => {});
      navigate(schoolRoute(row.key, params.grade), { state: { viaGrade: params.grade, year: shownYear } });
    } else if (level === 'school') {
      void logEvent('growth.teacher_opened.v1').catch(() => {});
      navigate(teacherRoute(row.key, params.school), { state: { viaGrade, year: shownYear, group: state.group } });
    }
  };

  const openStudent = (id: string) => {
    void logEvent('growth.student_opened.v1').catch(() => {});
    navigate(studentRoute(id), { state: { viaGrade, school: schoolParam, teacher: params.teacher, year: shownYear, group: state.group } });
  };

  return (
    <Shell crumbs={crumbs}>
      {loaded.kind === 'loading' && <Loading />}
      {loaded.kind === 'failed' && <LoadFailure tokens={GROWTH} />}
      {loaded.kind === 'notFound' && <NotFound text={NOT_FOUND[level]} />}
      {loaded.kind === 'ready' && (
        <GrowthBody
          page={loaded.data}
          level={level}
          skill={skill}
          onSkill={setSkill}
          group={group}
          onGroup={setGroup}
          year={year}
          onYear={setYear}
          onOpen={open}
          onOpenStudent={openStudent}
          onRemoveGrade={() => navigate(schoolRoute(params.school ?? ''), { state: { viaGrade, year: shownYear, group: state.group } })}
        />
      )}
    </Shell>
  );
}

function GrowthBody({
  page,
  level,
  skill,
  onSkill,
  group,
  onGroup,
  year,
  onYear,
  onOpen,
  onOpenStudent,
  onRemoveGrade,
}: {
  page: Page;
  level: GrowthLevel;
  skill: Skill;
  onSkill: (skill: Skill) => void;
  group: GroupBy;
  onGroup: (group: GroupBy) => void;
  year: number | undefined;
  onYear: (year: number | undefined) => void;
  onOpen: (row: GrowthRow) => void;
  onOpenStudent: (id: string) => void;
  onRemoveGrade: () => void;
}) {
  const [filters, setFilters] = useState<StudentFilters>(DEFAULT_FILTERS);
  const period = page.period;
  const later = laterLabel(period);
  const skillName = SKILL_LABELS[skill];
  const years = page.years.map((value) => ({ value, label: yearLabel(value) }));
  const selectedYear = year ?? period?.schoolYear ?? page.years[0];
  const bySchool = level === 'district' && group === 'school';
  const rows = level === 'district' ? (group === 'grade' ? page.byGrade : group === 'band' ? page.byBand : shownSchools(page, skill)) : page.groups;
  const firstColumn = level === 'district' ? FIRST_COLUMN[group] : level === 'grade' ? 'School' : 'Teacher';
  const exportCsv = () => {
    void logEvent('growth.exported.v1').catch(() => {});
    if (level === 'teacher') {
      const { shown } = visibleStudents(page.studentRows, filters.classId, filters.query, filters.hideNoLater);
      downloadCsv(`flashlight-360-growth-${slug(page.teacher ?? page.title)}.csv`, buildTeacherCsv(page, shown));
      return;
    }
    const scope = level === 'district' ? '' : `${slug(page.title)}-`;
    downloadCsv(`flashlight-360-growth-${scope}by-${slug(firstColumn)}-${skill}.csv`, buildGroupCsv(sortLeastGrowthFirst(rows, skill), skill, firstColumn, period?.growthLabel ?? 'On track', { averages: !bySchool }));
  };
  const actions = (
    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
      {years.length > 0 && selectedYear !== undefined && <LabelledSelect label="School year" value={selectedYear} options={years} onChange={(value) => onYear(value)} />}
      {period && <ActionButton label={level === 'teacher' ? 'Export CSV' : 'Export'} onClick={exportCsv} />}
    </Box>
  );
  const header = <PageHeader title={page.title} subtitle={subtitleOf(page.subtitle, period, page.dataAsOf)} action={actions} />;
  if (!period) {
    return (
      <>
        {header}
        <NoResults />
      </>
    );
  }
  const totals = page.totals[skill];
  const matched = totals.matched;
  const total = totals.students || page.students;
  const here = level === 'school';
  const gradeWord = level === 'grade' ? `Grade ${page.grade ?? ''} ` : level === 'school' && page.grade ? `Grade ${page.grade} ` : '';
  const tableSub = period.open
    ? `${later} open · Matched so far: ${matched} of ${total}`
    : `Matched students: eligible BOY and ${later}${here ? ' here' : ''} · ${matched} of ${total}`;
  const gridSub = here
    ? `${skillName} · ${matched} ${gradeWord}students who took BOY and ${later} here`
    : `${skillName} · ${matched} ${gradeWord}students with a BOY and ${later === 'EOY' ? 'an EOY' : 'a MOY'} score${period.open ? ' so far' : ''}`;
  const groupOptions = level === 'district' ? GROUP_OPTIONS.filter((option) => option.value !== 'school' || page.bySchool !== null) : [{ value: group, label: firstColumn }];
  const yearChoice = yearChoiceOf(page);
  const controls = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
      <SkillTabs value={skill} onChange={onSkill} />
      <LabelledSelect label="Group by" value={group} options={groupOptions} onChange={onGroup} />
      {yearChoice && <LabelledSelect label="Growth" value={yearChoice} options={YEAR_CHOICES} onChange={(choice) => onYear(choice === 'this' ? page.currentYear : page.currentYear - 1)} />}
      {level === 'school' && page.grade && <FilterChip label={`Grade ${page.grade}`} onRemove={onRemoveGrade} />}
    </Box>
  );
  if (level === 'teacher') {
    return (
      <>
        {header}
        <StudentsTable page={page} later={later} filters={filters} onFilters={setFilters} onSelect={onOpenStudent} />
      </>
    );
  }
  return (
    <>
      {header}
      {controls}
      {level !== 'grade' && <ReturnTestingCard figures={totals} later={later} here={here} open={period.open} />}
      <GroupTable
        title={`By ${firstColumn.toLowerCase()}`}
        sub={tableSub}
        firstColumn={firstColumn}
        nameWidth={level === 'district' && !bySchool ? 132 : 200}
        rows={rows}
        skill={skill}
        period={period}
        scheme={page.scheme}
        onSelect={onOpen}
        showAssignments={level === 'school'}
        highlightLowest={level === 'school'}
        sortedNote={level !== 'district' || bySchool}
        stackedLabel={level === 'school'}
        hideAverage={bySchool}
        emptyText={`No ${firstColumn.toLowerCase()}s have students with results.`}
      />
      <BandGrid grid={totals.grid} scheme={page.scheme} later={later} sub={gridSub} open={period.open} />
      {(level === 'district' || (level === 'school' && !page.grade)) && <BandHistogram grid={totals.grid} scheme={page.scheme} skill={skill} later={later} sub={gridSub} open={period.open} />}
    </>
  );
}

export function GrowthStudentPage({ source = fetchGrowthStudent }: { source?: StudentSource }) {
  const location = useLocation();
  const { studentId = '' } = useParams();
  const state = trailState(location.state);
  const year = state.year;
  const load = useCallback((id: string) => source(id, year), [source, year]);
  const loaded = useLoaded(studentId, load);
  const crumbs: Trail | undefined =
    loaded.kind === 'ready' ? { level: 'student', title: loaded.data.name, school: state.school, teacher: state.teacher, viaGrade: state.viaGrade, year: year ?? loaded.data.period?.schoolYear, group: state.group } : undefined;
  return (
    <Shell crumbs={crumbs}>
      {loaded.kind === 'loading' && <Loading />}
      {loaded.kind === 'failed' && <LoadFailure tokens={GROWTH} />}
      {loaded.kind === 'notFound' && <NotFound text={NOT_FOUND.student} />}
      {loaded.kind === 'ready' && <StudentBody student={loaded.data} />}
    </Shell>
  );
}

function StudentBody({ student }: { student: GrowthStudent }) {
  const parts = [`Grade ${student.grade}`, student.teachers.join(', '), student.school].filter((part) => part.trim() !== '' && part !== 'Grade ');
  const stamp = formatDataAsOf(student.dataAsOf);
  const subtitle = stamp ? `${parts.join(' · ')} · ${stamp}` : parts.join(' · ');
  const plan = student.planRoute ? (
    <Box
      component={RouterLink}
      to={student.planRoute}
      sx={{ display: 'inline-flex', alignItems: 'center', p: '8px 16px', borderRadius: '8px', bgcolor: GROWTH.action, color: GROWTH.cardBg, fontFamily: GROWTH.font, fontSize: '16px', lineHeight: '24px', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#151547' } }}
    >
      Open action plan
    </Box>
  ) : undefined;
  return (
    <>
      <PageHeader title={student.name} subtitle={subtitle} action={plan} />
      <BenchmarkHistory history={student.history} scheme={student.scheme} period={student.period} />
      <RubricIndicators rubric={student.rubric} scheme={student.scheme} period={student.period} />
      <Responses responses={student.responses} scheme={student.scheme} />
    </>
  );
}
